import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "section__container" }
const _hoisted_2 = { class: "section__text" }
const _hoisted_3 = { class: "section__input__container" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { class: "section__text" }

import { i18n } from '@/i18n';
import { computed, StyleValue } from 'vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { storeToRefs } from 'pinia';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import SimpleSelectInput from '@/corePagesModule/components/SelectInput.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { commonModalTextMapper } from '../constants';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import TextIconButton from '@/corePagesModule/components/TextIconButton.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'AddOptionSection',
  props: {
    options: { default: () => [] },
    disabled: { type: Boolean }
  },
  emits: ["setOption"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "0579a1ae": (newOptionGridTemplateColumnNumber.value)
}))

const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);
const { setNewOptionItemType, getAnotherAddingItem, checkDuplicationOfNewOption } = store;

const t = i18n.t;
// eslint-disable-next-line jsdoc/require-jsdoc
const props = __props;

const emits = __emit;

const VALUE_ONLY_OPTIONS = computed(() => [t('package'), t('manufacturer'), t('endUser')]);

/**
 * 表示する選択肢の数を決定するための変数
 */
const newOptionGridTemplateColumnNumber = computed(() => {
  return state.value.newOption.isAnotherInputShow ? 2 : 1;
});

/**
 * フォーム用の選択肢項目定義
 */
const newOptionFieldItems = computed((): FieldItem[] => {
  const targetArray = ['value', 'option', 'anotherValue', 'anotherOption'];
  const items = targetArray.map((name: string) => {
    return {
      name,
      rules: [ValidateRule.Required],
    };
  });
  return items;
});

/**
 * バリデーションエラー発生時にテキスト/ボタンの位置を調整するためのスタイル
 */
const textStyle = computed(
  (): StyleValue => (Object.values(newOptionFieldState).some((state) => state.isError.value) ? { paddingTop: '12px' } : {})
);
const buttonStyle = computed(
  (): StyleValue => (Object.values(newOptionFieldState).some((state) => state.isError.value) ? { marginTop: '12px' } : {})
);

/**
 * フォーム変数
 */
const {
  fieldState: newOptionFieldState,
  allValidate: newOptionAllValidate,
  resetForm: newOptionResetForm,
} = useForms(newOptionFieldItems.value);

/**
 * 新規選択肢追加処理で、追加ボタンが有効かどうか
 */
const isNewOptionAddButtonActive = computed(() => {
  if (VALUE_ONLY_OPTIONS.value.includes(newOptionFieldState['option'].value.value)) {
    return newOptionFieldState['value'].value.value && newOptionFieldState['option'].value.value;
  }
  return (
    newOptionFieldState['value'].value.value &&
    newOptionFieldState['option'].value.value &&
    newOptionFieldState['anotherValue'].value.value &&
    newOptionFieldState['anotherOption'].value.value
  );
});

/**
 * 新規選択肢追加のinputに入力した際の処理
 * @param e - 変更された値
 * @param kind - どのinputに入力されたか
 */
const updatedNewOptionInput = (e: string, kind: 'value' | 'option' | 'anotherValue') => {
  if (kind === 'option') {
    if (e === newOptionFieldState['option'].value.value) return;
    setNewOptionItemType(e);
    if (!e || VALUE_ONLY_OPTIONS.value.includes(e)) {
      state.value.newOption.isAnotherInputShow = false;
      const value = newOptionFieldState['value'].value.value;
      newOptionResetForm(newOptionFieldItems.value);
      newOptionFieldState['value'].setValue(value);
      newOptionFieldState['option'].setValue(e);
    } else {
      const value = newOptionFieldState['value'].value.value;
      newOptionResetForm(newOptionFieldItems.value);
      newOptionFieldState['value'].setValue(value);
      newOptionFieldState['option'].setValue(e);
      newOptionFieldState['anotherOption'].setValue(getAnotherAddingItem(e) ?? '');
      state.value.newOption.isAnotherInputShow = true;
    }
  } else {
    newOptionFieldState[kind].setValue(e);
  }
};

/**
 * 新規選択肢追加ボタンを押した際の処理
 */
const clickNewOptionAddButton = async () => {
  /**
   * テキストがundefinedだった場合に空文字に変換する関数
   * @param text - テキスト
   */
  const _checkUndefinedText = (text?: string) => {
    return text ? t(text) : '';
  };

  const validateResult = VALUE_ONLY_OPTIONS.value.includes(newOptionFieldState['option'].value.value)
    ? newOptionFieldState['value'].validate(newOptionFieldState['value'].value.value).isValid
    : newOptionAllValidate();
  if (!validateResult) return;

  let value: string, code: string;
  let isAnotherValueCode: boolean;
  if (
    [
      t('warehouseName'),
      t('supplierName'),
      t('businessName'),
      t('package'),
      t('customerName'),
      t('salesDestinationName'),
      t('manufacturer'),
      t('endUser'),
    ].includes(newOptionFieldState['option'].value.value)
  ) {
    isAnotherValueCode = true;
    value = newOptionFieldState['value'].value.value;
    code = newOptionFieldState['anotherValue'].value.value;
  } else {
    isAnotherValueCode = false;
    value = newOptionFieldState['anotherValue'].value.value;
    code = newOptionFieldState['value'].value.value;
  }
  const checkDuplicationResponse = await waitLoading(
    checkDuplicationOfNewOption(value, code),
    _checkUndefinedText(commonModalTextMapper.newOption.loading?.title)
  );
  if (!checkDuplicationResponse) {
    await showInfoModal({
      modalTitle: _checkUndefinedText(commonModalTextMapper.newOption.commonError?.title),
      modalDescription: _checkUndefinedText(commonModalTextMapper.newOption.commonError?.description),
      backButtonLabel: t('確認'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  }
  if (checkDuplicationResponse.existsResult) {
    await showInfoModal({
      modalTitle: _checkUndefinedText(commonModalTextMapper.newOption.duplicateError?.title),
      modalDescription: _checkUndefinedText(commonModalTextMapper.newOption.duplicateError?.description),
      backButtonLabel: t('確認'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    // 重複チェックに引っかかった場合
    if (checkDuplicationResponse.existsValue.toString() === value) {
      newOptionFieldState[isAnotherValueCode ? 'value' : 'anotherValue'].isError.value = true;
      newOptionFieldState[isAnotherValueCode ? 'value' : 'anotherValue'].errorMessage.value = t('重複しています');
    }
    if (checkDuplicationResponse.existsCode === code) {
      newOptionFieldState[isAnotherValueCode ? 'anotherValue' : 'value'].isError.value = true;
      newOptionFieldState[isAnotherValueCode ? 'anotherValue' : 'value'].errorMessage.value = t('重複しています');
    }
  } else {
    const newOptionItemType = state.value.newOption.itemType === t('item') ? 'business' : state.value.newOption.itemType;
    emits('setOption', newOptionItemType, value, code);
    newOptionResetForm(newOptionFieldItems.value);
  }
  return;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)('項目へ新規選択肢を追加')), 1),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(TextInput, {
          class: "section__input",
          "model-value": _unref(newOptionFieldState)['value'].value.value,
          label: "",
          placeholder: _unref(t)('新規選択肢'),
          "is-error": _unref(newOptionFieldState)['value'].isError.value,
          "error-message": _unref(newOptionFieldState)['value'].errorMessage.value,
          disabled: _ctx.disabled,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = 
            (e) => {
              updatedNewOptionInput(e, 'value');
            }
          )
        }, null, 8, ["model-value", "placeholder", "is-error", "error-message", "disabled"]),
        _createElementVNode("span", {
          style: _normalizeStyle(textStyle.value)
        }, _toDisplayString(_unref(t)('を')), 5),
        _createVNode(SimpleSelectInput, {
          class: "section__input",
          "model-value": _unref(newOptionFieldState)['option'].value.value,
          index: 99,
          label: "",
          options: _ctx.options,
          "select-box-name": "SELECT_COLUMN_FOR_NEW_SELECTION",
          placeholder: _unref(t)('必須項目から選択'),
          "display-allow": "",
          disabled: _ctx.disabled,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = 
            (e) => {
              updatedNewOptionInput(e, 'option');
            }
          )
        }, null, 8, ["model-value", "options", "placeholder", "disabled"]),
        _createElementVNode("span", {
          style: _normalizeStyle(textStyle.value)
        }, [
          _createTextVNode(_toDisplayString(_unref(t)('として')), 1),
          (_unref(state).newOption.isAnotherInputShow)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(t)('、')), 1))
            : _createCommentVNode("", true)
        ], 4),
        (_unref(state).newOption.isAnotherInputShow)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(TextInput, {
                class: "section__input",
                "model-value": _unref(newOptionFieldState)['anotherValue'].value.value,
                label: "",
                placeholder: _unref(t)('新規選択肢'),
                "is-error": _unref(newOptionFieldState)['anotherValue'].isError.value,
                "error-message": _unref(newOptionFieldState)['anotherValue'].errorMessage.value,
                disabled: _ctx.disabled,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = 
              (e) => {
                updatedNewOptionInput(e, 'anotherValue');
              }
            )
              }, null, 8, ["model-value", "placeholder", "is-error", "error-message", "disabled"]),
              _createElementVNode("span", {
                style: _normalizeStyle(textStyle.value)
              }, _toDisplayString(_unref(t)('を')), 5),
              _createVNode(TextInput, {
                class: "section__input",
                "model-value": _unref(newOptionFieldState)['anotherOption'].value.value,
                label: "",
                disabled: ""
              }, null, 8, ["model-value"]),
              _createElementVNode("span", {
                style: _normalizeStyle(textStyle.value)
              }, _toDisplayString(_unref(t)('として')), 5)
            ], 64))
          : _createCommentVNode("", true),
        _createVNode(TextIconButton, {
          style: _normalizeStyle(buttonStyle.value),
          text: _unref(t)('追加'),
          icon: "plusIconWhite",
          "prop-disabled": !isNewOptionAddButtonActive.value,
          onClick: clickNewOptionAddButton
        }, null, 8, ["style", "text", "prop-disabled"])
      ]),
      _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(t)('上記のセクションに新規選択肢を追加できます。')), 1)
    ])
  ]))
}
}

})